import { useI18n } from 'vue-i18n';
import { usePrimeVue } from 'primevue/config';
import { watch } from 'vue';

/**
 * Composable para sincronizar la configuración de locale de PrimeVue con i18n
 * Se actualiza automáticamente cuando cambia el idioma
 */
export function usePrimeVueLocale() {
  const { t, locale } = useI18n();
  const primeVue = usePrimeVue();

  // Función para obtener configuración de locale con traducciones
  const getLocaleConfig = () => ({
    firstDayOfWeek: 1,
    dayNames: [
      t("wkt.client.common.days_week.sunday"),
      t("wkt.client.common.days_week.monday"),
      t("wkt.client.common.days_week.tuesday"),
      t("wkt.client.common.days_week.wednesday"),
      t("wkt.client.common.days_week.thursday"),
      t("wkt.client.common.days_week.friday"),
      t("wkt.client.common.days_week.saturday"),
    ],
    dayNamesShort: [
      t("wkt.client.common.days_week.sunday_short"),
      t("wkt.client.common.days_week.monday_short"),
      t("wkt.client.common.days_week.tuesday_short"),
      t("wkt.client.common.days_week.wednesday_short"),
      t("wkt.client.common.days_week.thursday_short"),
      t("wkt.client.common.days_week.friday_short"),
      t("wkt.client.common.days_week.saturday_short"),
    ],
    dayNamesMin: [
      t("wkt.client.common.days_week.sunday_min"),
      t("wkt.client.common.days_week.monday_min"),
      t("wkt.client.common.days_week.tuesday_min"),
      t("wkt.client.common.days_week.wednesday_min"),
      t("wkt.client.common.days_week.thursday_min"),
      t("wkt.client.common.days_week.friday_min"),
      t("wkt.client.common.days_week.saturday_min"),
    ],
    monthNames: [
      t("wkt.client.common.month-january"),
      t("wkt.client.common.month-february"),
      t("wkt.client.common.month-march"),
      t("wkt.client.common.month-april"),
      t("wkt.client.common.month-may"),
      t("wkt.client.common.month-june"),
      t("wkt.client.common.month-july"),
      t("wkt.client.common.month-august"),
      t("wkt.client.common.month-september"),
      t("wkt.client.common.month-october"),
      t("wkt.client.common.month-november"),
      t("wkt.client.common.month-december"),
    ],
    monthNamesShort: [
      t("wkt.client.common.month-january_short"),
      t("wkt.client.common.month-february_short"),
      t("wkt.client.common.month-march_short"),
      t("wkt.client.common.month-april_short"),
      t("wkt.client.common.month-may_short"),
      t("wkt.client.common.month-june_short"),
      t("wkt.client.common.month-july_short"),
      t("wkt.client.common.month-august_short"),
      t("wkt.client.common.month-september_short"),
      t("wkt.client.common.month-october_short"),
      t("wkt.client.common.month-november_short"),
      t("wkt.client.common.month-december_short"),
    ],
    today: t("wkt.client.common.today"),
    clear: t("wkt.client.common.clear"),
    weekHeader: t("wkt.client.common.weekHeader"),
    accept: t("wkt.client.common.accept"),
    reject: t("wkt.client.common.reject"),
    cancel: t("wkt.client.common.cancel"),
    choose: t("wkt.client.common.choose"),
    upload: t("wkt.client.common.upload"),
    aria: {
      trueLabel: t("wkt.client.common.aria.trueLabel"),
      falseLabel: t("wkt.client.common.aria.falseLabel"),
      nullLabel: t("wkt.client.common.aria.nullLabel"),
    },
  });

  // Observar cambios en el locale de i18n y actualizar PrimeVue
  watch(
    () => locale.value,
    () => {
      // PrimeVue config.locale acepta propiedades adicionales en tiempo de ejecución
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (primeVue.config as Record<string, any>).locale = getLocaleConfig();
    }
  );

  return {
    getLocaleConfig,
  };
}
